import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * The workshop parts store.
 *
 * Stock on hand is never stored - it is derived from the movements, so any quantity on
 * screen can be traced back to the purchases and issues that produced it. Issuing a part
 * to a bus is what turns it into a cost against that vehicle, which is why an issue must
 * name one.
 */
function ManageParts() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [tab, setTab] = useState("items");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [stock, setStock] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [belowReorderOnly, setBelowReorderOnly] = useState(false);

  const blankItem = {
    Part_ID: 0,
    Name: "",
    Category: "",
    Unit: "",
    Reorder_Level: "",
    Remarks: "",
    Is_Active: true,
  };
  const blankTxn = {
    ID: 0,
    Part_ID: "",
    Txn_Type: "In",
    Txn_Date: moment().format("YYYY-MM-DD"),
    Qty: "",
    Unit_Price: "",
    Vehicle_ID: "",
    Vendor: "",
    Invoice_No: "",
    Remarks: "",
  };

  const [showItem, setShowItem] = useState(false);
  const [showTxn, setShowTxn] = useState(false);
  const [item, setItem] = useState(blankItem);
  const [txn, setTxn] = useState(blankTxn);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const loadItems = useCallback(() => {
    setLoading(true);
    post("Part/GetItems", auth({ Name: search, Category: category, BelowReorderOnly: belowReorderOnly }))
      .then((res) => {
        if (res.data.status_code === 1) {
          setItems(res.data.ListofRecords || []);
          setSummary(res.data.Summary || null);
          setCategories(res.data.Categories || []);
        } else {
          setItems([]);
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setLoading(false));
  }, [post, auth, search, category, belowReorderOnly]);

  const loadStock = useCallback(() => {
    setLoading(true);
    post("Part/GetStock", auth({}))
      .then((res) => {
        if (res.data.status_code === 1) setStock(res.data.ListofRecords || []);
        else setStock([]);
      })
      .catch(() => setStock([]))
      .finally(() => setLoading(false));
  }, [post, auth]);

  useEffect(() => {
    document.title = "Parts & Store";
    scrollToTop();
    post("Vehicle/GetVehicles", auth({ Is_Active: true }))
      .then((res) => {
        if (res.data.status_code === 1) setVehicles(res.data.ListofRecords || []);
      })
      .catch(() => setVehicles([]));
  }, [post, auth]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (tab === "stock") loadStock();
  }, [tab, loadStock]);

  const money = (n) =>
    n === null || n === undefined
      ? "—"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const qty = (n) =>
    n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  /* ---- item form ---- */

  const openAddItem = () => {
    setItem(blankItem);
    setValidated(false);
    setShowItem(true);
  };

  const openEditItem = (p) => {
    setItem({
      Part_ID: p.Part_ID,
      Name: p.Name,
      Category: p.Category || "",
      Unit: p.Unit || "",
      Reorder_Level: p.Reorder_Level ?? "",
      Remarks: p.Remarks || "",
      Is_Active: p.Is_Active,
    });
    setValidated(false);
    setShowItem(true);
  };

  const saveItem = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "Part/SaveItem",
      auth({
        Part_ID: item.Part_ID,
        Name: item.Name,
        Category: item.Category,
        Unit: item.Unit,
        Reorder_Level: item.Reorder_Level === "" ? null : parseFloat(item.Reorder_Level),
        Remarks: item.Remarks,
        Is_Active: item.Is_Active,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowItem(false);
          loadItems();
        } else {
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setSaving(false));
  };

  const deleteItem = (p) => {
    if (!window.confirm(`Delete "${p.Name}" from the catalogue?`)) return;
    post("Part/DeleteItem", auth({ Part_ID: p.Part_ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          loadItems();
        } else {
          // Most often: the part has stock movements, so its history explains a bus cost.
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 10000,
          });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  /* ---- stock movement form ---- */

  const openAddTxn = (partId) => {
    setTxn({ ...blankTxn, Part_ID: partId ? String(partId) : "" });
    setValidated(false);
    setShowTxn(true);
  };

  const saveTxn = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "Part/SaveStock",
      auth({
        ID: txn.ID,
        Part_ID: parseInt(txn.Part_ID, 10),
        Txn_Type: txn.Txn_Type,
        Txn_Date: txn.Txn_Date,
        Qty: txn.Qty === "" ? 0 : parseFloat(txn.Qty),
        Unit_Price: txn.Unit_Price === "" ? null : parseFloat(txn.Unit_Price),
        Vehicle_ID: txn.Vehicle_ID ? parseInt(txn.Vehicle_ID, 10) : null,
        Vendor: txn.Vendor,
        Invoice_No: txn.Invoice_No,
        Remarks: txn.Remarks,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowTxn(false);
          if (tab === "items") loadItems();
          else loadStock();
        } else {
          // Issuing more than the shelf holds lands here.
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setSaving(false));
  };

  const deleteTxn = (t) => {
    if (
      !window.confirm(
        `Delete the ${moment(t.Txn_Date).format("DD-MMM-YYYY")} ${t.Txn_Type.toLowerCase()} of ${t.PartName}?`
      )
    ) {
      return;
    }
    post("Part/DeleteStock", auth({ ID: t.ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          loadStock();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  const renderItems = () => {
    if (items.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="text-center text-muted py-4">
            No parts in the store
          </td>
        </tr>
      );
    }

    return items.map((p, i) => (
      <tr key={p.Part_ID}>
        <td>{i + 1}</td>
        <td className="fw-bold">{p.Name}</td>
        <td>{p.Category || "—"}</td>
        <td>{p.Unit || "—"}</td>
        <td className="text-end">{qty(p.QtyIn)}</td>
        <td className="text-end">{qty(p.QtyIssued)}</td>
        <td className="text-end fw-bold">
          {qty(p.OnHand)}
          {p.NeedsReorder ? (
            <span
              className="badge bg-warning text-dark ms-2"
              title={`At or below the reorder level of ${qty(p.Reorder_Level)}`}
            >
              reorder
            </span>
          ) : null}
        </td>
        <td className="text-end">{money(p.StockValue)}</td>
        <td>
          <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
            <li>
              <button
                type="button"
                className="green"
                title="Edit part"
                onClick={() => openEditItem(p)}
              >
                <i className="ri-edit-line"></i>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="blue"
                title="Record stock movement"
                onClick={() => openAddTxn(p.Part_ID)}
              >
                <i className="ri-add-line"></i>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="red"
                title="Delete part"
                onClick={() => deleteItem(p)}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          </ul>
        </td>
      </tr>
    ));
  };

  const renderStock = () => {
    if (stock.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="text-center text-muted py-4">
            No stock movements recorded
          </td>
        </tr>
      );
    }

    return stock.map((t, i) => (
      <tr key={t.ID}>
        <td>{i + 1}</td>
        <td className="text-nowrap">{moment(t.Txn_Date).format("DD-MMM-YYYY")}</td>
        <td>
          <span
            className={`badge ${
              t.Txn_Type === "In"
                ? "bg-success"
                : t.Txn_Type === "Issue"
                ? "bg-danger"
                : "bg-info text-dark"
            }`}
          >
            {t.Txn_Type}
          </span>
        </td>
        <td className="fw-bold">{t.PartName}</td>
        <td className="text-end">
          {qty(t.Qty)} {t.Unit}
        </td>
        <td className="text-end">{t.Unit_Price ? money(t.Unit_Price) : "—"}</td>
        <td className="text-end">{money(t.LineValue)}</td>
        <td>{t.Registration_No || (t.Txn_Type === "In" ? "—" : "not set")}</td>
        <td>
          <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
            <li>
              <button
                type="button"
                className="red"
                title="Delete movement"
                onClick={() => deleteTxn(t)}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          </ul>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/TransportDashboard">Transport</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Parts &amp; Store
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Parts &amp; Store</div>
                <p className="text-muted small mb-0">
                  Stock on hand is worked out from the movements, so every quantity can be
                  explained. Issuing a part to a bus makes it a cost against that bus.
                </p>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <button
                    type="button"
                    onClick={() => openAddTxn(null)}
                    className="button button-white button-icon"
                  >
                    Stock Movement
                    <i className="ri-swap-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={openAddItem}
                    className="button button-white button-icon"
                  >
                    Add Part
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Parts in catalogue</div>
                    <div className="h4 mb-0">{summary ? summary.Items : 0}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Stock value on hand</div>
                    <div className="h4 mb-0">
                      Rs. {summary ? money(summary.StockValue) : 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Issued to buses</div>
                    <div className="h4 mb-0">
                      Rs. {summary ? money(summary.IssuedValue) : 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Below reorder level</div>
                    <div
                      className={`h4 mb-0 ${
                        summary && summary.BelowReorder > 0 ? "text-danger" : ""
                      }`}
                    >
                      {summary ? summary.BelowReorder : 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${tab === "items" ? "active" : ""}`}
                      onClick={() => setTab("items")}
                    >
                      Inventory
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${tab === "stock" ? "active" : ""}`}
                      onClick={() => setTab("stock")}
                    >
                      Stock movements
                    </button>
                  </li>
                </ul>

                {tab === "items" ? (
                  <div className="row g-2 mb-3 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Search</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="part or category"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Category</label>
                      <select
                        className="form-select"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        <option value="">All categories</option>
                        {categories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="belowReorder"
                          checked={belowReorderOnly}
                          onChange={(e) => setBelowReorderOnly(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="belowReorder">
                          Needs reordering only
                        </label>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="table-responsive">
                  <table className="table table-theme">
                    <thead>
                      {tab === "items" ? (
                        <tr>
                          <th>Sr.</th>
                          <th>Part</th>
                          <th>Category</th>
                          <th>Unit</th>
                          <th className="text-end">In</th>
                          <th className="text-end">Issued</th>
                          <th className="text-end">On hand</th>
                          <th className="text-end">Value</th>
                          <th>Action</th>
                        </tr>
                      ) : (
                        <tr>
                          <th>Sr.</th>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Part</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Unit price</th>
                          <th className="text-end">Value</th>
                          <th>Bus</th>
                          <th>Action</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : tab === "items" ? (
                        renderItems()
                      ) : (
                        renderStock()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* part catalogue entry */}
      <Modal
        className="modal-theme"
        show={showItem}
        onHide={() => setShowItem(false)}
        centered
      >
        <Form noValidate validated={validated} onSubmit={saveItem}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px" }}>
              {item.Part_ID ? "Edit part" : "Add part"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">
                  Part name <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="text"
                  required
                  maxLength={300}
                  value={item.Name}
                  onChange={(e) => setItem({ ...item, Name: e.target.value })}
                />
                <Form.Control.Feedback type="invalid">
                  Enter the part name.
                </Form.Control.Feedback>
              </div>
              <div className="col-md-6">
                <label className="form-label">Category</label>
                <Form.Control
                  type="text"
                  maxLength={200}
                  list="partCategories"
                  value={item.Category}
                  onChange={(e) => setItem({ ...item, Category: e.target.value })}
                />
                <datalist id="partCategories">
                  {categories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="col-md-6">
                <label className="form-label">Unit</label>
                <Form.Control
                  type="text"
                  maxLength={60}
                  placeholder="piece, litre, set"
                  value={item.Unit}
                  onChange={(e) => setItem({ ...item, Unit: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Reorder level</label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={item.Reorder_Level}
                  onChange={(e) => setItem({ ...item, Reorder_Level: e.target.value })}
                />
                <div className="form-text">Flagged once stock falls to this.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <Form.Select
                  value={item.Is_Active ? "1" : "0"}
                  onChange={(e) => setItem({ ...item, Is_Active: e.target.value === "1" })}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </Form.Select>
              </div>
              <div className="col-12">
                <label className="form-label">Remarks</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={1000}
                  value={item.Remarks}
                  onChange={(e) => setItem({ ...item, Remarks: e.target.value })}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="button button-light"
              onClick={() => setShowItem(false)}
            >
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : item.Part_ID ? "Update Part" : "Add Part"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* stock movement */}
      <Modal
        className="modal-theme"
        show={showTxn}
        onHide={() => setShowTxn(false)}
        size="lg"
        centered
      >
        <Form noValidate validated={validated} onSubmit={saveTxn}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px" }}>Stock movement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">
                  Part <span className="text-danger">*</span>
                </label>
                <Form.Select
                  required
                  value={txn.Part_ID}
                  onChange={(e) => setTxn({ ...txn, Part_ID: e.target.value })}
                >
                  <option value="">-- select --</option>
                  {items.map((p) => (
                    <option key={p.Part_ID} value={p.Part_ID}>
                      {p.Name} ({qty(p.OnHand)} {p.Unit} on hand)
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Select a part.
                </Form.Control.Feedback>
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  Movement <span className="text-danger">*</span>
                </label>
                <Form.Select
                  required
                  value={txn.Txn_Type}
                  onChange={(e) => setTxn({ ...txn, Txn_Type: e.target.value })}
                >
                  <option value="In">Stock in (purchase)</option>
                  <option value="Issue">Issue to bus</option>
                  <option value="Return">Return to store</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  Date <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="date"
                  required
                  max={moment().format("YYYY-MM-DD")}
                  value={txn.Txn_Date}
                  onChange={(e) => setTxn({ ...txn, Txn_Date: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">
                  Quantity <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0.01}
                  required
                  value={txn.Qty}
                  onChange={(e) => setTxn({ ...txn, Qty: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Unit price</label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={txn.Unit_Price}
                  onChange={(e) => setTxn({ ...txn, Unit_Price: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Bus{" "}
                  {txn.Txn_Type === "Issue" ? <span className="text-danger">*</span> : null}
                </label>
                <Form.Select
                  disabled={txn.Txn_Type === "In"}
                  value={txn.Vehicle_ID}
                  onChange={(e) => setTxn({ ...txn, Vehicle_ID: e.target.value })}
                >
                  <option value="">
                    {txn.Txn_Type === "In" ? "not applicable to a purchase" : "-- select --"}
                  </option>
                  {vehicles.map((v) => (
                    <option key={v.Vehicle_ID} value={v.Vehicle_ID}>
                      {v.Registration_No}
                    </option>
                  ))}
                </Form.Select>
                {txn.Txn_Type === "Issue" ? (
                  <div className="form-text">
                    This is what makes the part a cost against the bus.
                  </div>
                ) : null}
              </div>

              <div className="col-md-6">
                <label className="form-label">Vendor</label>
                <Form.Control
                  type="text"
                  maxLength={300}
                  value={txn.Vendor}
                  onChange={(e) => setTxn({ ...txn, Vendor: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Invoice no.</label>
                <Form.Control
                  type="text"
                  maxLength={100}
                  value={txn.Invoice_No}
                  onChange={(e) => setTxn({ ...txn, Invoice_No: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Remarks</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={1000}
                  value={txn.Remarks}
                  onChange={(e) => setTxn({ ...txn, Remarks: e.target.value })}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="button button-light"
              onClick={() => setShowTxn(false)}
            >
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : "Record Movement"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default ManageParts;
